"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import moment from "moment";
import { toast } from "react-toastify";
import {
  adminArticleList,
  adminArticleSelectStateUpdate,
  ArticleState,
} from "@/entities";
import { AdminPagination, Error, Loader, getFileUrl } from "@/shared";
import { AdminArticlesFilter } from "@/features";

export function AdminArticlesList() {
  const router = useRouter();
  const params = useParams<{ slug: string }>();
  const searchParams = useSearchParams();

  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: adminArticleSelectStateUpdate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`admin-articles`, { ...filterParams, slug, page, perPage }],
      });
      toast.success("글 상태가 변경되었습니다.");

      const checkers = document.getElementsByName(
        "ids"
      ) as NodeListOf<HTMLInputElement>;
      checkers.forEach((checker) => (checker.checked = false));
      setIds([]);
    },
    onError: (e: any) => {
      toast.error(`에러(${e.message})가 발생했습니다. 다시 시도해 주세요.`);
    },
  });

  const slug = params?.slug || "";
  const state = searchParams?.get("state") || "";
  const common_category_id = searchParams?.get("common_category_id") || "";
  const keyword = searchParams?.get("keyword") || "";
  const page = parseInt(searchParams?.get("page") || "1");

  const perPage = 15;
  const filterParams = { state, common_category_id, keyword };

  const { error, isLoading, data } = useQuery({
    queryKey: [`admin-articles`, { ...filterParams, slug, page, perPage }],
    queryFn: () => adminArticleList({ ...filterParams, slug, page, perPage }),
  });

  const [ids, setIds] = useState([] as string[]);
  const doAllChecker = (e: any) => {
    const checked = e.target.checked;

    const checkers = document.getElementsByName(
      "ids"
    ) as NodeListOf<HTMLInputElement>;

    checkers.forEach((checker) => {
      checker.checked = checked ? true : false;
    });

    if (checked) {
      const all = data?.result.map((item) => item.id.toString());
      setIds(all ?? []);
    } else {
      setIds([]);
    }
  };
  const doChecker = (e: any) => {
    const checked = e.target.checked;
    const value = e.target.value;

    if (checked) {
      setIds([...ids, value]);
    } else {
      setIds(ids.filter((id) => id !== value));
    }
  };
  const doActive = () => {
    // console.log(ids);
    if (!confirm("활성화 하시겠습니까?")) return;
    if (updateMutation.isPending) return;
    updateMutation.mutate({ ids, state: "active" });
  };
  const doInactive = () => {
    // console.log(ids);
    if (!confirm("차단 하시겠습니까?")) return;
    if (updateMutation.isPending) return;
    updateMutation.mutate({ ids, state: "inactive" });
  };
  const doDelete = () => {
    // console.log(ids);
    if (!confirm("삭제 하시겠습니까?")) return;
    if (updateMutation.isPending) return;
    updateMutation.mutate({ ids, state: "delete" });
  };
  useEffect(() => {
    const allChecker = document.getElementById(
      "allChecker"
    ) as HTMLInputElement;

    if (data?.result && data?.result.length > 0) {
      if (data?.result.length === ids.length) {
        allChecker.checked = true;
      } else {
        allChecker.checked = false;
      }
    }
  }, [ids]);

  if (error) return <Error error={error} />;
  if (isLoading) return <Loader className="my-[100px]" />;

  return (
    <>
      <AdminArticlesFilter />

      {data?.count && data?.count > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle text-center text-nowrap">
              <colgroup>
                <col width="5%" />
                <col width="5%" />
                <col width="70%" />
                <col width="10%" />
                <col width="10%" />
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="text-left align-middle"
                    colSpan={ids.length > 0 ? 5 : 1}
                  >
                    <span className="form-check">
                      <input
                        type="checkbox"
                        id="allChecker"
                        className="form-check-input"
                        onChange={doAllChecker}
                      />
                      {ids.length > 0 && (
                        <span className="form-check-label">
                          <button
                            type="button"
                            className="mx-2 disabled:opacity-50"
                            onClick={doActive}
                            disabled={updateMutation.isPending}
                          >
                            활성
                          </button>
                          <button
                            type="button"
                            className="mx-2 disabled:opacity-50"
                            onClick={doInactive}
                            disabled={updateMutation.isPending}
                          >
                            차단
                          </button>
                          <button
                            type="button"
                            className="mx-2 disabled:opacity-50"
                            onClick={doDelete}
                            disabled={updateMutation.isPending}
                          >
                            삭제
                          </button>
                        </span>
                      )}
                    </span>
                  </th>
                  <th className={ids.length > 0 ? "hide" : ""}>상태</th>
                  <th className={ids.length > 0 ? "hide" : ""}>제목</th>
                  <th className={ids.length > 0 ? "hide" : ""}>등록자</th>
                  <th className={ids.length > 0 ? "hide" : ""}>등록일자</th>
                </tr>
              </thead>
              <tbody>
                {data.result.map((item: any, index: number) => {
                  const link = `/admin/${slug}/${
                    item.id
                  }?${searchParams?.toString()}`;
                  return (
                    <tr key={index}>
                      <td className="text-left">
                        <input
                          type="checkbox"
                          name="ids"
                          className="form-check-input"
                          onChange={doChecker}
                          value={item.id}
                        />
                      </td>
                      <td>{ArticleState(item.state)}</td>
                      <td
                        className="text-start cursor-pointer"
                        onClick={() => router.push(link)}
                      >
                        <div className="d-flex align-items-center">
                          <div className="w-50px h-50px bg-light d-flex align-items-center justify-content-center">
                            <Image
                              src={
                                item?.cover?.file_url
                                  ? getFileUrl(item?.cover?.file_url)
                                  : "/assets/images/default_user.png"
                              }
                              width={50}
                              height={50}
                              // sizes="(max-width: 50px) 5vw"
                              alt="프로필 이미지"
                            />
                          </div>
                          <div className="ms-3">
                            <div className="text-ellipsis">
                              {item.common_category?.name &&
                                `분류 : ${item.common_category?.name ?? "-"}`}
                            </div>
                            <div className="text-ellipsis">
                              제목 : {item.title}{" "}
                              {moment()
                                .add(-1, "days")
                                .isBefore(moment(item.created_at)) && (
                                <span className="badge bg-dark">New</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>{item.user.name}</td>
                      <td>
                        <div>
                          {new Date(item.created_at).toLocaleDateString("ko")}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="d-md-flex align-items-center justify-content-between">
            <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
              {data.count}개 항목 중 {(page - 1) * perPage + 1}에서{" "}
              {data.count < (page - 1) * perPage + perPage
                ? data.count
                : (page - 1) * perPage + perPage}
              까지 표시
            </div>
            <AdminPagination
              total={Math.ceil(data?.count / perPage)}
              page={page}
              pathname={`/admin/${slug}`}
              params={filterParams}
            />
          </div>
        </>
      ) : (
        <p className="m-5 text-center">등록된 데이터가 없습니다.</p>
      )}
    </>
  );
}
